import type { FC } from "react";
import { useCallback } from "react";
import type { LinkProps } from "expo-router";
import { Link } from "expo-router";
import {
  BookIcon,
  ChartContainer,
  ChartMentalBalance,
  computeRadiusByValue,
  getIndexes,
  getValue,
  RenderCircles,
  RenderValue,
  withIconRef,
} from "@ptsd/uikit";
import { Pie, PieChart, Sector } from "recharts";
import type { PieSectorDataItem } from "recharts/types/polar/Pie";
import { styled, Text, View, XStack, YStack } from "tamagui";

interface TextLabelProps {
  section: ChartMentalBalance.Config[ChartMentalBalance.Domain];
  value: number;
}
const TextLabel: FC<TextLabelProps> = ({ section, value = 0 }) => {
  return (
    <Text
      fontFamily={"$subtitle"}
      fontWeight={"$4"}
      fontSize={13.5}
      lineHeight={"$2"}
      color={"$gray-950"}
    >
      {section.label} {value}
    </Text>
  );
};

interface TextTagProps {
  section: ChartMentalBalance.Config[ChartMentalBalance.Domain];
}

const TextTag: FC<TextTagProps> = ({ section }) => {
  return (
    <Text
      fontFamily={"$subtitle"}
      fontWeight={"$4"}
      fontSize={"$1"}
      lineHeight={"$2"}
      borderRadius={"$5"}
      paddingHorizontal={"$1"}
      backgroundColor={section.color + "4D"}
      color={section.color}
      width={"max-content"}
    >
      {section.tag}
    </Text>
  );
};

const BookIconMobile = styled(withIconRef(BookIcon), { width: 24, height: 24 });

const DomainLabel: FC<{
  domain: ChartMentalBalance.Domain;
  position: "left" | "right";
  disabled?: boolean;
  getValue: (domain: ChartMentalBalance.Domain) => number;
}> = ({ position, domain, getValue, disabled = false }) => {
  const section = ChartMentalBalance.config[domain];
  const value = getValue(domain);

  const handlePress = useCallback<Exclude<LinkProps["onPress"], undefined>>(
    (e) => disabled && e.preventDefault(),
    [disabled],
  );

  return (
    <YStack
      gap={"$0.5"}
      alignItems={position === "left" ? "flex-end" : "flex-start"}
      width={"50%"}
      flex={1}
    >
      <Link href={`/results/${domain}`} onPress={handlePress}>
        <XStack
          gap={"$1"}
          alignItems="center"
          justifyContent={position === "left" ? "flex-end" : "flex-start"}
        >
          {position === "left" && <BookIconMobile />}
          <TextLabel section={section} value={value} />
          {position === "right" && <BookIconMobile />}
        </XStack>
      </Link>
      <TextTag section={section} />
    </YStack>
  );
};

interface MentalBalanceMobilePieChartProps {
  data: { domain: string; slice: number; fill: string; value: number }[];
  disabledLabelLink?: boolean;
}

export const MentalBalanceMobilePieChart: FC<
  MentalBalanceMobilePieChartProps
> = ({ data, disabledLabelLink = false }) => {
  const getDomainValue = useCallback(
    (domain: ChartMentalBalance.Domain) =>
      data.find((s) => s.domain === domain)?.value ?? 0,
    [data],
  );

  return (
    <View>
      <YStack gap={"$2"}>
        <XStack flex={2} gap={"$3"} justifyContent={"space-between"}>
          <DomainLabel
            domain="confidence"
            position="left"
            disabled={disabledLabelLink}
            getValue={getDomainValue}
          />
          <DomainLabel
            domain="physicalcondition"
            position="right"
            disabled={disabledLabelLink}
            getValue={getDomainValue}
          />
        </XStack>

        <XStack flex={2} gap={"$3"} justifyContent={"space-between"}>
          <DomainLabel
            domain="performance"
            position="left"
            disabled={disabledLabelLink}
            getValue={getDomainValue}
          />
          <DomainLabel
            domain="selfcontrol"
            position="right"
            disabled={disabledLabelLink}
            getValue={getDomainValue}
          />
        </XStack>
      </YStack>
      <ChartContainer
        width={"100%"}
        maxWidth={400}
        height={340}
        config={ChartMentalBalance.config}
      >
        {({ width, height }) => {
          const activeIndex = getIndexes(data);
          const outerRadius = Math.floor(width / 3);
          const innerRadius = Math.floor(outerRadius / 6);
          return (
            <PieChart>
              <Pie
                data={ChartMentalBalance.outerSectors}
                dataKey={"slice"}
                nameKey={"domain"}
                outerRadius={outerRadius}
                innerRadius={innerRadius}
                cx="50%"
                cy="50%"
                strokeWidth={0}
                activeIndex={activeIndex}
                activeShape={(props: PieSectorDataItem) => (
                  <RenderCircles
                    layout="md"
                    data={data}
                    domain={props.name}
                    {...props}
                    containerHeight={height}
                    containerWidth={width}
                  />
                )}
              />

              <Pie
                activeIndex={activeIndex}
                activeShape={(props: PieSectorDataItem) => {
                  const { cx, cy, midAngle, name } = props;

                  const dynamicOuterRadius = computeRadiusByValue(
                    getValue("domain", name, data)?.value,
                    outerRadius,
                  );

                  return (
                    <>
                      <Sector {...props} outerRadius={dynamicOuterRadius} />
                      <RenderValue
                        cx={cx ?? 0}
                        cy={cy ?? 0}
                        innerRadius={innerRadius}
                        midAngle={midAngle ?? 0}
                        domain={name}
                        value={getValue("domain", name, data)?.value}
                        dynamicOuterRadius={dynamicOuterRadius}
                      />
                    </>
                  );
                }}
                strokeWidth={0}
                innerRadius={innerRadius}
                data={data}
                dataKey="slice"
                nameKey="domain"
                outerRadius={0}
                cx="50%"
                cy="50%"
              />
            </PieChart>
          );
        }}
      </ChartContainer>

      <YStack gap={"$2"}>
        <XStack flex={2} gap={"$3"} justifyContent={"space-between"}>
          <DomainLabel
            domain="balance"
            position="left"
            disabled={disabledLabelLink}
            getValue={getDomainValue}
          />
          <DomainLabel
            domain="lustforlife"
            position="right"
            disabled={disabledLabelLink}
            getValue={getDomainValue}
          />
        </XStack>

        <XStack flex={2} gap={"$3"} justifyContent={"space-between"}>
          <DomainLabel
            domain="relationships"
            position="left"
            disabled={disabledLabelLink}
            getValue={getDomainValue}
          />
          <DomainLabel
            domain="stressresistance"
            position="right"
            disabled={disabledLabelLink}
            getValue={getDomainValue}
          />
        </XStack>
      </YStack>
    </View>
  );
};
